import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { FileText, Sparkles, Eye, Send, Loader2, ExternalLink } from 'lucide-react';

export default function Answers() {
  const queryClient = useQueryClient();
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [previewAnswer, setPreviewAnswer] = useState<any>(null);

  const { data: questions } = useQuery({
    queryKey: ['questions', '', '', 'pending'],
    queryFn: () => api.getQuestions({ status: 'pending' })
  });

  const { data: answers, isLoading } = useQuery({
    queryKey: ['answers', selectedStatus],
    queryFn: () => api.getAnswers({ status: selectedStatus || undefined })
  });

  const generateMutation = useMutation({
    mutationFn: (questionId: string) => api.generateAnswer(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['answers'] });
      queryClient.invalidateQueries({ queryKey: ['questions'] });
    }
  });

  const publishMutation = useMutation({
    mutationFn: (answerId: string) => api.publishAnswer(answerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['answers'] });
    }
  });

  const bulkGenerateMutation = useMutation({
    mutationFn: (questionIds: string[]) => api.generateBulkAnswers(questionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['answers'] });
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      setSelectedQuestions([]);
    }
  });

  const statusColors: Record<string, string> = {
    draft: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800',
    review: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800',
    published: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800',
    archived: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'
  };

  const pendingQuestions = Array.isArray(questions) ? questions : [];
  const answerList = Array.isArray(answers) ? answers : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Answers</h1>
          <p className="text-gray-500">Generate and manage answer content</p>
        </div>
      </div>

      {/* Generate from Pending Questions */}
      {pendingQuestions.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Pending Questions ({pendingQuestions.length})
            </h2>
            <button
              onClick={() => {
                if (selectedQuestions.length > 0) {
                  bulkGenerateMutation.mutate(selectedQuestions);
                }
              }}
              disabled={selectedQuestions.length === 0 || bulkGenerateMutation.isPending}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
            >
              {bulkGenerateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Selected ({selectedQuestions.length})
                </>
              )}
            </button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pendingQuestions.slice(0, 10).map((q: any) => (
              <label
                key={q.id}
                className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
              >
                <input
                  type="checkbox"
                  checked={selectedQuestions.includes(q.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedQuestions([...selectedQuestions, q.id]);
                    } else {
                      setSelectedQuestions(selectedQuestions.filter(id => id !== q.id));
                    }
                  }}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-3 text-sm text-gray-900 flex-1 truncate">
                  {q.question_text}
                </span>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    generateMutation.mutate(q.id);
                  }}
                  disabled={generateMutation.isPending}
                  className="ml-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Generate
                </button>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center space-x-4">
          <label className="label mb-0">Status:</label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="input w-auto"
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="review">Review</option>
            <option value="published">Published</option>
          </select>
        </div>
      </div>

      {/* Answers List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="space-y-4">
          {answerList.length > 0 ? (
            answerList.map((answer: any) => (
              <div key={answer.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                      <FileText className="h-5 w-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 mb-1 truncate">
                        {answer.content?.meta?.title || 'Untitled Answer'}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                        {answer.content?.content?.direct_answer?.summary}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className={statusColors[answer.status] || 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'}>
                          {answer.status}
                        </span>
                        <span className="text-gray-500">
                          {answer.word_count} words
                        </span>
                        <span className="text-gray-500">
                          v{answer.version}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setPreviewAnswer(answer)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Preview"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    {answer.status !== 'published' && (
                      <button
                        onClick={() => publishMutation.mutate(answer.id)}
                        disabled={publishMutation.isPending}
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                        title="Publish"
                      >
                        <Send className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="card text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No answers yet</h3>
              <p className="text-gray-500">Generate answers from pending questions above</p>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewAnswer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Answer Preview</h2>
              <button
                onClick={() => setPreviewAnswer(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                &times;
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <article className="prose max-w-none">
                <h1>{previewAnswer.content?.content?.h1_question}</h1>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
                  <p className="font-medium text-blue-900">
                    {previewAnswer.content?.content?.direct_answer?.summary}
                  </p>
                </div>
                <ul>
                  {previewAnswer.content?.content?.direct_answer?.key_points?.map((point: string, i: number) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
                {previewAnswer.content?.content?.detailed_sections?.map((section: any, i: number) => (
                  <div key={i}>
                    <h2>{section.heading}</h2>
                    <p>{section.content}</p>
                  </div>
                ))}
                <h2>Quick Facts</h2>
                <table className="w-full">
                  <tbody>
                    {previewAnswer.content?.content?.quick_facts_table?.map((fact: any, i: number) => (
                      <tr key={i}>
                        <th className="text-left py-2 pr-4">{fact.label}</th>
                        <td>{fact.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <h2>Frequently Asked Questions</h2>
                {previewAnswer.content?.content?.faqs?.map((faq: any, i: number) => (
                  <div key={i} className="mb-4">
                    <h3>{faq.question}</h3>
                    <p>{faq.answer}</p>
                  </div>
                ))}
              </article>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
